import React, { useState, useEffect } from 'react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import usePermissions from '../../hooks/usePermissions';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function authHeaders() {
  const h = {};
  if (window.__sessionId) h['X-Session-ID'] = window.__sessionId;
  return h;
}

export default function CatalogSettings() {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('catalog-settings', 'create'); // Generate/regenerate QR
  const canEdit = hasPermission('catalog-settings', 'edit');     // Toggle access, base URL, session duration
  const [duration, setDuration] = useState('');
  const [savedDuration, setSavedDuration] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [qrError, setQrError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenMsg, setRegenMsg] = useState('');

  // Catalog base URL (editable)
  const [catalogBaseUrl, setCatalogBaseUrl] = useState('');
  const [urlSaving, setUrlSaving] = useState(false);
  const [urlMsg, setUrlMsg] = useState('');

  // Catalog access toggle + expiry
  const [catalogEnabled, setCatalogEnabled] = useState(true);
  const [catalogExpiresAt, setCatalogExpiresAt] = useState(''); // ISO datetime-local string
  const [accessSaving, setAccessSaving] = useState(false);
  const [accessMsg, setAccessMsg] = useState('');

  const loadQr = (baseUrl) => {
    setQrError(false);
    setQrDataUrl(null);
    const baseUrlParam = encodeURIComponent(baseUrl || catalogBaseUrl);
    fetch(`${API_BASE}/public/catalog/qr?base_url=${baseUrlParam}`, {
      credentials: 'include',
      headers: authHeaders()
    })
      .then(r => {
        if (!r.ok) throw new Error('Failed');
        return r.blob();
      })
      .then(blob => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }))
      .then(dataUrl => setQrDataUrl(dataUrl))
      .catch(() => setQrError(true));
  };

  useEffect(() => {
    fetch(`${API_BASE}/public/catalog/settings`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        setSavedDuration(d.session_duration_minutes);
        setDuration(String(d.session_duration_minutes));
        setCatalogEnabled(d.catalog_enabled !== false);
        if (d.catalog_expires_at) {
          const dt = new Date(d.catalog_expires_at);
          const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000)
            .toISOString().slice(0, 16);
          setCatalogExpiresAt(local);
        } else {
          setCatalogExpiresAt('');
        }
        // Seed the URL field: prefer saved DB value, fall back to current browser origin
        const resolved = d.catalog_base_url || process.env.REACT_APP_CATALOG_URL || window.location.origin;
        setCatalogBaseUrl(resolved);
        loadQr(resolved);
      })
      .catch(() => {
        const fallback = process.env.REACT_APP_CATALOG_URL || window.location.origin;
        setCatalogBaseUrl(fallback);
        loadQr(fallback);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    const val = parseInt(duration);
    if (isNaN(val) || val < 1 || val > 10080) {
      setSaveMsg('Enter a value between 1 and 10,080 minutes (1 week).');
      return;
    }
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await fetch(`${API_BASE}/public/catalog/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify({ session_duration_minutes: val })
      });
      if (!res.ok) throw new Error('Failed');
      setSavedDuration(val);
      setSaveMsg('Saved successfully.');
    } catch {
      setSaveMsg('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAccessSave = async () => {
    setAccessSaving(true);
    setAccessMsg('');
    try {
      const body = { catalog_enabled: catalogEnabled };
      // Send expiry as UTC ISO string, or null to clear
      body.catalog_expires_at = catalogExpiresAt
        ? new Date(catalogExpiresAt).toISOString()
        : null;

      const res = await fetch(`${API_BASE}/public/catalog/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error('Failed');
      setAccessMsg('Saved successfully.');
    } catch {
      setAccessMsg('Failed to save. Please try again.');
    } finally {
      setAccessSaving(false);
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm(
      'Regenerate the QR access token?\n\nThis will immediately invalidate all previously printed QR codes. You will need to reprint the new QR code before using it at your next exhibition.'
    )) return;

    setRegenerating(true);
    setRegenMsg('');
    try {
      const res = await fetch(`${API_BASE}/public/catalog/access-token/regenerate`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed');
      setRegenMsg('Token regenerated. Reload the QR below and reprint before your next exhibition.');
      loadQr(catalogBaseUrl);
    } catch {
      setRegenMsg('Failed to regenerate. Please try again.');
    } finally {
      setRegenerating(false);
    }
  };

  const handleUrlSave = async () => {
    const url = catalogBaseUrl.trim().replace(/\/$/, '');
    if (!url) return;
    setUrlSaving(true);
    setUrlMsg('');
    try {
      const res = await fetch(`${API_BASE}/public/catalog/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify({ catalog_base_url: url })
      });
      if (!res.ok) throw new Error('Failed');
      setCatalogBaseUrl(url);
      setUrlMsg('Saved. Refreshing QR…');
      loadQr(url);
      setTimeout(() => setUrlMsg(''), 3000);
    } catch {
      setUrlMsg('Failed to save. Please try again.');
    } finally {
      setUrlSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${catalogBaseUrl}/catalog`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const handlePrintQR = () => {
    if (!qrDataUrl) return;
    const html = `<!DOCTYPE html>
<html>
<head>
  <title>Customer Catalog QR Code — GDS Jewellers</title>
  <style>
    body { font-family: 'Segoe UI', sans-serif; text-align: center; padding: 40px; }
    h1 { font-size: 28px; margin-bottom: 4px; color: #1a1a1a; }
    p { color: #666; font-size: 14px; margin-bottom: 24px; }
    img { width: 280px; height: 280px; border: 8px solid #1a1a1a; border-radius: 12px; }
    @media print { button { display: none; } }
  </style>
</head>
<body>
  <h1>GDS Jewellers</h1>
  <p>Scan to browse our exclusive collection</p>
  <img src="${qrDataUrl}" alt="Catalog QR Code" />
  <br/><button onclick="window.print()">Print</button>
</body>
</html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    setTimeout(() => {
      if (win) win.print();
      URL.revokeObjectURL(url);
    }, 600);
  };

  const handleDownload = () => {
    if (!qrDataUrl) return;
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = 'gds-catalog-qr.png';
    link.click();
  };

  const durationLabel = (mins) => {
    if (!mins) return '';
    if (mins < 60) return `${mins} minute${mins !== 1 ? 's' : ''}`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}h ${m}m` : `${h} hour${h !== 1 ? 's' : ''}`;
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-1">Customer Catalog</h1>
      <p className="text-gray-500 text-sm mb-6">
        QR code for exhibition use — customers scan once to browse all available inventory.
      </p>

      {/* QR Code card */}
      <div className="bg-white rounded-xl shadow border border-gray-200 p-6 mb-6">
        <h2 className="text-base font-semibold text-gray-700 mb-4">QR Code</h2>

        <div className="flex flex-col sm:flex-row gap-6 items-center">
          {/* QR image */}
          <div className="flex-shrink-0 flex flex-col items-center gap-2">
            <div className="w-48 h-48 border-4 border-gray-900 rounded-xl overflow-hidden bg-gray-50 flex items-center justify-center">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="Catalog QR Code"
                  className="w-full h-full object-contain"
                />
              ) : qrError ? (
                <div className="text-center p-4">
                  <p className="text-xs text-gray-400">Failed to load QR</p>
                  <button
                    onClick={loadQr}
                    className="mt-2 text-xs text-blue-500 underline"
                  >Retry</button>
                </div>
              ) : (
                <div className="text-center p-4">
                  <p className="text-xs text-gray-400">Loading…</p>
                </div>
              )}
            </div>
            {qrDataUrl && (
              <span className="text-xs text-green-600 font-medium">✓ Ready to print</span>
            )}
          </div>

          {/* Actions + info */}
          <div className="flex-1 min-w-0">
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Catalog Base URL
              </label>
              <p className="text-xs text-gray-400 mb-1">
                Update this whenever your server IP changes, then save to regenerate the QR.
              </p>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="url"
                  value={catalogBaseUrl}
                  onChange={e => setCatalogBaseUrl(e.target.value)}
                  placeholder="http://192.168.5.126:3001"
                  readOnly={!canEdit}
                  className={`text-sm border rounded-lg px-3 py-2 flex-1 focus:outline-none ${canEdit ? 'bg-gray-50 border-gray-200 text-gray-700 focus:ring-2 focus:ring-blue-400' : 'bg-gray-100 border-gray-100 text-gray-500'}`}
                />
                {canEdit && (
                  <button
                    onClick={handleUrlSave}
                    disabled={urlSaving}
                    className="px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                  >
                    {urlSaving ? 'Saving…' : 'Save & Refresh QR'}
                  </button>
                )}
              </div>
              {urlMsg && (
                <p className={`text-xs mb-2 font-medium ${urlMsg.includes('Failed') ? 'text-red-600' : 'text-green-600'}`}>
                  {urlMsg}
                </p>
              )}
              <div className="flex items-center gap-2">
                <code className="text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 flex-1 break-all text-gray-500">
                  {catalogBaseUrl}/catalog
                </code>
                <button
                  onClick={handleCopy}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 hover:bg-gray-50 whitespace-nowrap"
                >
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
              <strong>Access-controlled.</strong> The QR encodes a secret token — customers who
              type the URL directly are blocked. Only QR scanners can access the catalog.
            </div>

            <div className="flex gap-3 flex-wrap">
              <button
                onClick={handlePrintQR}
                disabled={!qrDataUrl}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-700 disabled:opacity-40 transition-colors"
              >
                🖨️ Print QR Code
              </button>
              <button
                onClick={handleDownload}
                disabled={!qrDataUrl}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                ⬇ Download PNG
              </button>
            </div>
          </div>
        </div>

        {/* Regenerate token */}
        {canCreate && (
          <div className="mt-6 pt-5 border-t border-gray-100">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-gray-700">Regenerate Access Token</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Only do this if the QR has been compromised. All existing printed QR codes will stop working immediately.
                </p>
              </div>
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors whitespace-nowrap"
              >
                {regenerating ? 'Regenerating…' : 'Regenerate Token'}
              </button>
            </div>
            {regenMsg && (
              <p className={`mt-3 text-sm font-medium ${regenMsg.includes('regenerated') ? 'text-amber-600' : 'text-red-600'}`}>
                {regenMsg}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Catalog Access toggle + expiry */}
      <div className="bg-white rounded-xl shadow border border-gray-200 p-6 mb-6">
        <h2 className="text-base font-semibold text-gray-700 mb-1">Catalog Access</h2>
        <p className="text-sm text-gray-500 mb-5">
          Control whether customers can access the catalog via the QR code.
          When disabled, anyone who scans will see a "Catalog Unavailable" message.
        </p>

        {/* Enable / Disable toggle */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-sm font-semibold text-gray-700">Enable Customer Access</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {catalogEnabled ? 'Customers can currently browse the catalog.' : 'Catalog is currently hidden from customers.'}
            </p>
          </div>
          <button
            onClick={() => canEdit && setCatalogEnabled(v => !v)}
            disabled={!canEdit}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${catalogEnabled ? 'bg-green-500' : 'bg-gray-300'} ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label="Toggle catalog access"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${catalogEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {canEdit && (
          <>
            {/* Auto-disable expiry */}
            <div className="mb-5">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Auto-Disable On <span className="normal-case font-normal text-gray-400">(optional)</span>
              </label>
              <p className="text-xs text-gray-400 mb-2">
                Leave blank to keep access open indefinitely. When this date/time passes, the catalog automatically disables.
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  type="datetime-local"
                  value={catalogExpiresAt}
                  onChange={e => setCatalogExpiresAt(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                {catalogExpiresAt && (
                  <button
                    onClick={() => setCatalogExpiresAt('')}
                    className="text-xs text-red-500 hover:text-red-700 underline"
                  >
                    Clear
                  </button>
                )}
              </div>
              {catalogExpiresAt && (
                <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  ⏱ Catalog will auto-disable on{' '}
                  <strong>{new Date(catalogExpiresAt).toLocaleString()}</strong>
                </p>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleAccessSave}
                disabled={accessSaving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {accessSaving ? 'Saving…' : 'Save Access Settings'}
              </button>
              {/* Live status badge */}
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${catalogEnabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${catalogEnabled ? 'bg-green-500' : 'bg-red-500'}`} />
                {catalogEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>

            {accessMsg && (
              <p className={`mt-3 text-sm font-medium ${accessMsg.includes('uccess') ? 'text-green-600' : 'text-red-600'}`}>
                {accessMsg}
              </p>
            )}
          </>
        )}
      </div>

      {/* Session duration */}
      {canEdit && (
      <div className="bg-white rounded-xl shadow border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-700 mb-1">Guest Session Duration</h2>
        <p className="text-sm text-gray-500 mb-4">
          How long a customer can browse after entering their details.
          {savedDuration && (
            <span className="ml-1 font-medium text-gray-700">
              Currently: <span className="text-blue-600">{durationLabel(savedDuration)}</span>
            </span>
          )}
        </p>

        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Duration (minutes)
            </label>
            <input
              type="number"
              value={duration}
              onChange={e => setDuration(e.target.value)}
              min="1"
              max="10080"
              className="w-36 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="e.g. 60"
            />
          </div>
          <div className="text-sm text-gray-400 pb-2">
            {duration && !isNaN(parseInt(duration)) ? durationLabel(parseInt(duration)) : ''}
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>

        {saveMsg && (
          <p className={`mt-3 text-sm font-medium ${saveMsg.includes('uccess') ? 'text-green-600' : 'text-red-600'}`}>
            {saveMsg}
          </p>
        )}

        <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs text-gray-500">
          <strong>Quick reference:</strong>&nbsp;
          60 = 1 hour · 480 = 8 hours · 1440 = 1 day · 10080 = 1 week
        </div>
      </div>
      )}
      <PageIdentifier pageId={SCREEN_IDS?.ADMINISTRATION?.CATALOG_SETTINGS || 'ADM-009'} pageName="Catalog Settings" />
    </div>
  );
}
